import { type Dispatch, type SetStateAction } from 'react';
import type { SendSchedule, Template, TemplateVariable, WABATemplate } from '@/api/templates';
import { Button, Input, Modal, Select, TenantSelector, Textarea } from '@/components/ui';
import { SEND_SCHEDULE_TYPES, TEMPLATE_CATEGORIES, TEMPLATE_FORM_CHANNEL_OPTIONS, TEMPLATE_TECH_TYPE_OPTIONS } from '@/features/templates/templateTaxonomy';
import { TemplateButtonsEditor } from './TemplateButtonsEditor';
import { TextAlternativeEditor } from './TextAlternativeEditor';
import { WABAButtonsPreview } from './WABAButtonsPreview';
import { OkdocScenarioProfileEditor } from './OkdocScenarioProfileEditor';

type WhatsappType = 'regular' | 'waba';
type WabaVariableMappingState = Record<string, { type: 'static' | 'dynamic'; value?: string; field?: string }>;

interface TemplateFormModalProps {
  isOpen: boolean;
  editingTemplate: string | null;
  isSuperAdmin: boolean;
  formData: Partial<Template>;
  setFormData: Dispatch<SetStateAction<Partial<Template>>>;
  variables: TemplateVariable[];
  insertVariable: (code: string) => void;
  onClose: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  whatsappType: WhatsappType;
  setWhatsappType: Dispatch<SetStateAction<WhatsappType>>;
  showWabaForAll: boolean;
  setShowWabaForAll: Dispatch<SetStateAction<boolean>>;
  wabaTemplates: WABATemplate[];
  isLoadingWabaTemplates: boolean;
  selectedWabaTemplate: WABATemplate | null;
  setSelectedWabaTemplate: Dispatch<SetStateAction<WABATemplate | null>>;
  wabaVariableMapping: WabaVariableMappingState;
  setWabaVariableMapping: Dispatch<SetStateAction<WabaVariableMappingState>>;
  wabaLoadError?: string;
}

export function TemplateFormModal({
  isOpen,
  editingTemplate,
  isSuperAdmin,
  formData,
  setFormData,
  variables,
  insertVariable,
  onClose,
  onSubmit,
  isSubmitting,
  isSubmitDisabled,
  whatsappType,
  setWhatsappType,
  showWabaForAll,
  setShowWabaForAll,
  wabaTemplates,
  isLoadingWabaTemplates,
  selectedWabaTemplate,
  setSelectedWabaTemplate,
  wabaVariableMapping,
  setWabaVariableMapping,
  wabaLoadError = '',
}: TemplateFormModalProps) {
  const isWabaOnlyMode = formData.messenger === 'whatsapp' && whatsappType === 'waba';
  const showOptionalWabaForAnyChannel =
    (!formData.messenger || formData.messenger === '' || formData.messenger === 'all') &&
    formData.template_type !== 'waba_template';
  const showWabaSelector =
    isWabaOnlyMode || (((!formData.messenger || formData.messenger === '' || formData.messenger === 'all') && showWabaForAll));
  const storedWabaMissing = Boolean(
    formData.external_template_id &&
    !isLoadingWabaTemplates &&
    !wabaLoadError &&
    wabaTemplates.length > 0 &&
    !wabaTemplates.some((template) =>
      template.template_guid === formData.external_template_id || template.id === formData.external_template_id),
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editingTemplate ? 'Редактировать шаблон' : 'Создать шаблон'}
      size="lg"
      closeOnBackdropClick={false}
      testId={editingTemplate ? 'modal-edit-template' : 'modal-create-template'}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            testId={editingTemplate ? 'btn-submit-edit-template' : 'btn-submit-create-template'}
          >
            {isSubmitting ? 'Сохранение...' : editingTemplate ? 'Сохранить' : 'Создать'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {isSuperAdmin && (
          <TenantSelector
            value={formData.tenant_id}
            onChange={(id) => setFormData((prev) => ({ ...prev, tenant_id: id }))}
            required
            testId="select-tenant-create"
          />
        )}
        <div className="rounded-lg border border-shell-border bg-shell-elevated p-3 text-sm text-shell-muted">
          Единая основа шаблона: название, контент, переменные, канал и тех. тип. Ниже добавляются только сценарии использования
          и канальные надстройки: WABA, кнопки, fallback и расписание.
        </div>
        <Input
          label="Название"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          required
          testId="input-name"
        />
        {!isWabaOnlyMode && (
          <div>
            <label className="block text-sm font-medium text-shell-text mb-1">
              Содержимое
            </label>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData((prev) => ({ ...prev, content: e.target.value }))}
                  rows={8}
                  placeholder="Введите текст шаблона. Используйте {{ПЕРЕМЕННАЯ}} для переменных."
                  testId="textarea-content"
                />
              </div>
              <div className="col-span-1">
                <div className="text-sm font-medium text-shell-text mb-2">Доступные переменные:</div>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {variables.map((variable) => (
                    <button
                      key={variable.id}
                      type="button"
                      onClick={() => insertVariable(variable.code)}
                      className="w-full text-left px-2 py-1 text-xs hover:bg-shell-hover rounded"
                      title={variable.description}
                    >
                      {variable.name} ({`{{${variable.code}}}`})
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <Select
          label="Тех. тип"
          value={formData.template_type}
          onChange={(e) => {
            const templateType = e.target.value;
            setFormData((prev) => ({ ...prev, template_type: templateType }));
            if (templateType === 'waba_template') {
              setFormData((prev) => ({ ...prev, template_type: templateType, messenger: 'whatsapp' }));
              setWhatsappType('waba');
            }
          }}
          options={TEMPLATE_TECH_TYPE_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          testId="select-template-type"
        />

        <Select
          label="Канал отправки"
          value={formData.messenger || ''}
          onChange={(e) => {
            const messenger = e.target.value;
            setFormData((prev) => ({ ...prev, messenger }));
            if (messenger !== 'whatsapp') {
              setWhatsappType('regular');
            }
          }}
          options={TEMPLATE_FORM_CHANNEL_OPTIONS}
          testId="select-messenger"
        />

        <Select
          label="Тематика"
          value={formData.category || ''}
          onChange={(e) => {
            const category = (e.target.value || undefined) as Template['category'];
            setFormData((prev) => ({
              ...prev,
              category,
              okdoc_scenario_profile: category === 'service' ? prev.okdoc_scenario_profile : undefined,
            }));
          }}
          options={[
            { value: '', label: 'Без тематики' },
            ...TEMPLATE_CATEGORIES.map((category) => ({
              value: category.value,
              label: category.label,
            })),
          ]}
          testId="select-category"
        />

        <div className="border border-shell-border rounded-lg p-4 space-y-3 bg-shell-elevated">
          <div className="text-sm font-medium text-shell-text flex items-center gap-2">
            Расписание отправки
          </div>
          <Select
            label="Тип расписания"
            value={formData.send_schedule?.type || ''}
            onChange={(e) => {
              const scheduleType = e.target.value as SendSchedule['type'];
              if (!scheduleType) {
                setFormData((prev) => ({ ...prev, send_schedule: undefined }));
                return;
              }
              setFormData((prev) => ({
                ...prev,
                send_schedule: {
                  ...prev.send_schedule,
                  type: scheduleType,
                  fallback_enabled: prev.send_schedule?.fallback_enabled ?? true,
                  stagger_enabled: prev.send_schedule?.stagger_enabled ?? true,
                  stagger_window_minutes: prev.send_schedule?.stagger_window_minutes || 60,
                  delay_minutes: prev.send_schedule?.delay_minutes || 5,
                  send_time: prev.send_schedule?.send_time || '10:00',
                  max_per_batch: prev.send_schedule?.max_per_batch || 100,
                },
              }));
            }}
            options={SEND_SCHEDULE_TYPES.map((item) => ({ value: item.value, label: item.label }))}
            testId="select-send-schedule-type"
          />
          {formData.send_schedule?.type === 'daily_scheduled' && (
            <div>
              <label className="block text-sm font-medium text-shell-text mb-1">
                Время отправки
              </label>
              <input
                type="time"
                value={formData.send_schedule?.send_time || '10:00'}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  send_schedule: { ...prev.send_schedule!, send_time: e.target.value },
                }))}
                className="block w-32 rounded-md border-shell-border shadow-sm focus:border-shell-primary focus:ring-shell-primary text-sm"
              />
              <p className="text-xs text-shell-muted mt-1">
                Рассылка запустится ежедневно в это время (по МСК)
              </p>
            </div>
          )}
          {formData.send_schedule?.type === 'event_triggered' && (
            <div>
              <label className="block text-sm font-medium text-shell-text mb-1">
                Задержка после события (мин)
              </label>
              <input
                type="number"
                min={0}
                max={1440}
                value={formData.send_schedule?.delay_minutes ?? 5}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  send_schedule: { ...prev.send_schedule!, delay_minutes: parseInt(e.target.value, 10) || 0 },
                }))}
                className="block w-32 rounded-md border-shell-border shadow-sm focus:border-shell-primary focus:ring-shell-primary text-sm"
              />
              <p className="text-xs text-shell-muted mt-1">
                Например, 5 мин после завершения приёма → отправка сбора ОС
              </p>
            </div>
          )}
          {formData.send_schedule?.type && formData.send_schedule.type !== 'manual' && (
            <>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="stagger_enabled"
                  checked={formData.send_schedule?.stagger_enabled ?? true}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    send_schedule: { ...prev.send_schedule!, stagger_enabled: e.target.checked },
                  }))}
                  className="h-4 w-4 text-shell-primary focus:ring-shell-primary rounded"
                />
                <label htmlFor="stagger_enabled" className="text-sm text-shell-text cursor-pointer">
                  Растягивать пакетную рассылку (Rate Limiting)
                </label>
              </div>
              {formData.send_schedule?.stagger_enabled && (
                <div className="ml-7 space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-shell-muted w-32">Окно (мин):</label>
                    <input
                      type="number"
                      min={5}
                      max={480}
                      value={formData.send_schedule?.stagger_window_minutes ?? 60}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        send_schedule: { ...prev.send_schedule!, stagger_window_minutes: parseInt(e.target.value, 10) || 60 },
                      }))}
                      className="block w-20 rounded-md border-shell-border shadow-sm focus:border-shell-primary focus:ring-shell-primary text-xs"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-shell-muted w-32">Макс. за batch:</label>
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={formData.send_schedule?.max_per_batch ?? 100}
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        send_schedule: { ...prev.send_schedule!, max_per_batch: parseInt(e.target.value, 10) || 100 },
                      }))}
                      className="block w-20 rounded-md border-shell-border shadow-sm focus:border-shell-primary focus:ring-shell-primary text-xs"
                    />
                  </div>
                  <p className="text-xs text-shell-muted">
                    Сообщения распределяются равномерно с учётом лимитов канала (WhatsApp ~40/мин, TG ~20/сек)
                  </p>
                </div>
              )}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="fallback_enabled"
                  checked={formData.send_schedule?.fallback_enabled ?? true}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    send_schedule: { ...prev.send_schedule!, fallback_enabled: e.target.checked },
                  }))}
                  className="h-4 w-4 text-shell-primary focus:ring-shell-primary rounded"
                />
                <label htmlFor="fallback_enabled" className="text-sm text-shell-text cursor-pointer">
                  Fallback Engine (переключение канала при недоставке)
                </label>
              </div>
            </>
          )}
        </div>

        {formData.messenger === 'whatsapp' && (
          <div>
            <label className="block text-sm font-medium text-shell-text mb-2">
              Тип WhatsApp
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="whatsapp_type"
                  value="regular"
                  checked={whatsappType === 'regular'}
                  onChange={(e) => setWhatsappType(e.target.value as WhatsappType)}
                  className="h-4 w-4 text-shell-primary focus:ring-shell-primary"
                />
                <span className="text-sm text-shell-text">
                  Regular (обычный WhatsApp)
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="whatsapp_type"
                  value="waba"
                  checked={whatsappType === 'waba'}
                  onChange={(e) => setWhatsappType(e.target.value as WhatsappType)}
                  className="h-4 w-4 text-shell-primary focus:ring-shell-primary"
                />
                <span className="text-sm text-shell-text">
                  WABA (WhatsApp Business API)
                </span>
              </label>
            </div>
          </div>
        )}

        {showOptionalWabaForAnyChannel && (
          <div className="border border-shell-border rounded-lg p-3 bg-shell-primary-soft">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showWabaForAll}
                onChange={(e) => {
                  setShowWabaForAll(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedWabaTemplate(null);
                    setWabaVariableMapping({});
                  }
                }}
                className="h-4 w-4 text-shell-primary focus:ring-shell-primary border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-shell-text">
                Также привязать WABA шаблон для WhatsApp Business
              </span>
            </label>
            <p className="text-xs text-shell-muted mt-1 ml-6">
              Если включено — при отправке через WABA будет использован одобренный шаблон из Meta,
              а через остальные каналы останется внутренний шаблон.
            </p>
          </div>
        )}

        {showWabaSelector && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-shell-text mb-1">
                WABA шаблон
              </label>
              {isLoadingWabaTemplates ? (
                <div className="text-sm text-shell-muted">Загрузка шаблонов кабинета этой клиники...</div>
              ) : wabaLoadError ? (
                <div className="p-3 bg-shell-danger-soft border border-shell-danger/30 rounded-lg">
                  <div className="text-sm text-shell-danger font-medium">Кабинет Wazzup этой клиники не открылся</div>
                  <div className="text-xs text-shell-danger mt-1">{wabaLoadError}</div>
                </div>
              ) : wabaTemplates.length === 0 ? (
                <div className="p-3 bg-shell-warning-soft border border-shell-warning/30 rounded-lg">
                  <div className="text-sm text-shell-warning font-medium">В кабинете Wazzup этой клиники нет одобренных шаблонов</div>
                </div>
              ) : (
                <Select
                  value={selectedWabaTemplate?.id || ''}
                  onChange={(e) => {
                    const template = wabaTemplates.find((item) => item.id === e.target.value) || null;
                    setSelectedWabaTemplate(template);
                    if (!template) {
                      return;
                    }
                    const mapping: WabaVariableMappingState = {};
                    template.variables.forEach((variableIndex) => {
                      mapping[variableIndex] = { type: 'static', value: '' };
                    });
                    setWabaVariableMapping(mapping);
                    setFormData((prev) => ({
                      ...prev,
                      external_template_id: template.template_guid,
                      content: template.body_text || '',
                      template_type: 'waba_template',
                      waba_variables_mapping: mapping,
                    }));
                  }}
                  options={[
                    { value: '', label: 'Выберите шаблон...' },
                    ...wabaTemplates.map((template) => ({
                      value: template.id,
                      label: `${template.name}${template.title ? ` (${template.title})` : ''} (${template.language})`,
                    })),
                  ]}
                  testId="select-waba-template"
                />
              )}
              <p className="text-xs text-shell-muted mt-1">
                Показываются только одобренные шаблоны кабинета Wazzup этой клиники
              </p>
              {storedWabaMissing && (
                <p className="text-xs text-shell-warning mt-2">
                  Сохранённый WABA-шаблон не найден в кабинете этой клиники. Выберите шаблон из списка, чужой кабинет сюда не подставляется.
                </p>
              )}
            </div>

            {selectedWabaTemplate && (
              <div className="border rounded-lg p-4 bg-shell-elevated">
                <div className="text-sm font-medium text-shell-text mb-2">Предпросмотр шаблона</div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 max-w-md">
                  {selectedWabaTemplate.header_text && (
                    <div className="font-semibold mb-2">{selectedWabaTemplate.header_text}</div>
                  )}
                  <div className="text-sm whitespace-pre-wrap mb-2">
                    {selectedWabaTemplate.body_text}
                  </div>
                  {selectedWabaTemplate.footer_text && (
                    <div className="text-xs text-shell-muted border-t pt-2 mt-2">
                      {selectedWabaTemplate.footer_text}
                    </div>
                  )}
                  {selectedWabaTemplate.buttons && selectedWabaTemplate.buttons.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {selectedWabaTemplate.buttons.map((button, index) => (
                        <button
                          key={index}
                          type="button"
                          className="text-xs px-2 py-1 bg-white border rounded hover:bg-shell-elevated"
                          disabled
                        >
                          {button.text}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {selectedWabaTemplate && selectedWabaTemplate.variables.length > 0 && (
              <div className="border rounded-lg p-4 bg-shell-elevated">
                <div className="text-sm font-medium text-shell-text mb-3">Параметры шаблона</div>
                <div className="space-y-4">
                  {selectedWabaTemplate.variables.map((variableIndex) => {
                    const mapping = wabaVariableMapping[variableIndex] || { type: 'static', value: '' };
                    return (
                      <div key={variableIndex} className="border-b pb-3 last:border-b-0">
                        <div className="text-sm font-medium text-shell-text mb-2">
                          Параметр {variableIndex} ({`{{${variableIndex}}}`})
                        </div>
                        <div className="flex gap-4 mb-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`waba_var_${variableIndex}_type`}
                              value="static"
                              checked={mapping.type === 'static'}
                              onChange={() => {
                                const nextMapping = {
                                  ...wabaVariableMapping,
                                  [variableIndex]: { type: 'static' as const, value: mapping.value || '' },
                                };
                                setWabaVariableMapping(nextMapping);
                                setFormData((prev) => ({ ...prev, waba_variables_mapping: nextMapping }));
                              }}
                              className="h-4 w-4 text-shell-primary"
                            />
                            <span className="text-sm">Статический текст</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`waba_var_${variableIndex}_type`}
                              value="dynamic"
                              checked={mapping.type === 'dynamic'}
                              onChange={() => {
                                const nextMapping = {
                                  ...wabaVariableMapping,
                                  [variableIndex]: { type: 'dynamic' as const, field: mapping.field || 'firstname_middlename' },
                                };
                                setWabaVariableMapping(nextMapping);
                                setFormData((prev) => ({ ...prev, waba_variables_mapping: nextMapping }));
                              }}
                              className="h-4 w-4 text-shell-primary"
                            />
                            <span className="text-sm">Поле пациента</span>
                          </label>
                        </div>
                        {mapping.type === 'static' ? (
                          <Input
                            value={mapping.value || ''}
                            onChange={(e) => {
                              const nextMapping = {
                                ...wabaVariableMapping,
                                [variableIndex]: { type: 'static' as const, value: e.target.value },
                              };
                              setWabaVariableMapping(nextMapping);
                              setFormData((prev) => ({ ...prev, waba_variables_mapping: nextMapping }));
                            }}
                            placeholder="Введите статический текст"
                            testId={`input-waba-var-${variableIndex}-static`}
                          />
                        ) : (
                          <Select
                            value={mapping.field || 'firstname_middlename'}
                            onChange={(e) => {
                              const nextMapping = {
                                ...wabaVariableMapping,
                                [variableIndex]: { type: 'dynamic' as const, field: e.target.value },
                              };
                              setWabaVariableMapping(nextMapping);
                              setFormData((prev) => ({ ...prev, waba_variables_mapping: nextMapping }));
                            }}
                            options={[
                              { value: 'firstname_middlename', label: 'Имя + Отчество' },
                              { value: 'firstname', label: 'Только имя' },
                              { value: 'middlename', label: 'Только отчество' },
                              { value: 'lastname', label: 'Фамилия' },
                              { value: 'fullname', label: 'ФИО полностью' },
                              { value: 'phone', label: 'Телефон' },
                            ]}
                            testId={`select-waba-var-${variableIndex}-dynamic`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {(formData.template_type === 'waba_template' || showWabaForAll) &&
          selectedWabaTemplate &&
          selectedWabaTemplate.buttons &&
          selectedWabaTemplate.buttons.length > 0 && (
            <WABAButtonsPreview buttons={selectedWabaTemplate.buttons} testId="waba-buttons-preview" />
          )}

        {formData.template_type !== 'waba_template' &&
          formData.messenger !== 'sms' &&
          !isWabaOnlyMode && (
            <TemplateButtonsEditor
              buttons={formData.buttons || []}
              onChange={(buttons) => setFormData((prev) => ({ ...prev, buttons }))}
              testId="template-buttons-editor"
            />
          )}

        <TextAlternativeEditor
          textAlternative={formData.text_alternative || ''}
          buttons={formData.buttons || []}
          onChange={(textAlternative) => setFormData((prev) => ({ ...prev, text_alternative: textAlternative }))}
          testId="text-alternative-editor"
        />

        {formData.category === 'service' && (
          <OkdocScenarioProfileEditor
            value={formData.okdoc_scenario_profile}
            buttons={formData.buttons || []}
            onChange={(okdocScenarioProfile) => setFormData((prev) => ({ ...prev, okdoc_scenario_profile: okdocScenarioProfile }))}
            testId="okdoc-scenario-profile-editor"
          />
        )}

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active ?? true}
            onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
            className="h-4 w-4 text-shell-primary focus:ring-shell-primary border-gray-300 rounded"
            data-testid="checkbox-is-active"
          />
          <label htmlFor="is_active" className="text-sm text-shell-text">
            Активен (шаблон доступен для использования)
          </label>
        </div>
      </div>
    </Modal>
  );
}